/**
 * Deterministic Pricing Engine v1
 * Produces a single final price from project configuration selections.
 * 
 * Future-ready hooks (not yet implemented):
 *  - margin enforcement
 *  - lead-source pricing influence
 *  - region / ZIP-based adjustments
 *  - crew cost adjustments
 */

import type { ProjectConfiguration, BathroomSystemType, BathroomMaterial } from "./types";
import type { ProductOption } from "./ProductOptionsContext";

/* ══════════════════════════════════════════════════════════
   Price tables – all values in USD
   ══════════════════════════════════════════════════════════ */

/** Base price per system type */
export const SYSTEM_BASE_PRICES: Record<BathroomSystemType, number> = {
  "Walk-in Shower": 12800,
  "Standard Tub": 11200,
  "Walk-in Tub": 16500,
  "Jetted Tub": 18900,
};

/** Dimensional presets (width × length in inches) → adjustment from the 30×60 baseline */
export interface DimensionPreset {
  label: string;
  width: number;
  length: number;
  adjustment: number;
}

export const DIMENSION_PRESETS: DimensionPreset[] = [
  { label: '30" × 60"', width: 30, length: 60, adjustment: 0 },
  { label: '32" × 60"', width: 32, length: 60, adjustment: 400 },
  { label: '36" × 48"', width: 36, length: 48, adjustment: 250 },
  { label: '36" × 60"', width: 36, length: 60, adjustment: 750 },
  { label: '36" × 72"', width: 36, length: 72, adjustment: 1400 },
];

/** Per-square-inch rate for truly custom sizes (above the 30×60 = 1800 sq-in baseline) */
const CUSTOM_SQIN_RATE = 0.65;
const BASELINE_SQIN = 30 * 60; // 1800

/** Material multiplier on base price */
export const MATERIAL_MULTIPLIERS: Record<BathroomMaterial, number> = {
  Sentrel: 1.0,   // baseline
  Dekton: 1.35,   // premium
  Tile: 1.18,     // mid-tier
};

/** Complexity factor flat adders */
export interface ComplexityFactors {
  laborComplexity: "standard" | "moderate" | "high";
  plumbingRelocation: boolean;
  customModifications: boolean;
}

const LABOR_COMPLEXITY_ADDERS: Record<ComplexityFactors["laborComplexity"], number> = {
  standard: 0,
  moderate: 1200,
  high: 2500,
};

const PLUMBING_RELOCATION_ADDER = 1800;
const CUSTOM_MODIFICATIONS_ADDER = 1200;

/* ══════════════════════════════════════════════════════════
   Price breakdown shape
   ══════════════════════════════════════════════════════════ */

export interface PriceBreakdown {
  baseSystemPrice: number;
  dimensionAdjustment: number;
  materialAdjustment: number;
  componentTotal: number;
  complexityAdjustment: number;
  manualAdjustment: number;
  manualAdjustmentNotes: string;
  /** Price before manual adjustment */
  calculatedPrice: number;
  finalTotal: number;
  // detail for display
  systemType: string;
  dimensions: string;
  material: string;
  componentDetails: { name: string; amount: number; pricingLabel?: string }[];
  complexityDetails: string[];
}

/* ══════════════════════════════════════════════════════════
   Parse a component's pricingImpact string → number
   e.g. "+$400" → 400, "Included" → 0, "Quote" → 0
   ══════════════════════════════════════════════════════════ */

export function parsePricingImpact(impact: string): number {
  if (!impact) return 0;
  const clean = impact.replace(/[^0-9.\-+]/g, "");
  return parseFloat(clean) || 0;
}

/* ══════════════════════════════════════════════════════════
   Dimension adjustment calculator
   ══════════════════════════════════════════════════════════ */

export function computeDimensionAdjustment(width: number, length: number): number {
  // Check presets first
  const preset = DIMENSION_PRESETS.find(p => p.width === width && p.length === length);
  if (preset) return preset.adjustment;

  // Custom: charge per extra square inch above baseline
  const sqIn = width * length;
  const delta = sqIn - BASELINE_SQIN;
  return Math.max(0, Math.round(delta * CUSTOM_SQIN_RATE));
}

/* ══════════════════════════════════════════════════════════
   Optional overrides from PricingContext
   ══════════════════════════════════════════════════════════ */

export interface PricingOverrides {
  systemBasePrices?: Record<string, number>;
  dimensionPresets?: DimensionPreset[];
  materialMultipliers?: Record<string, number>;
  complexityAdders?: {
    standard?: number;
    moderate?: number;
    high?: number;
    plumbingRelocation?: number;
    customModifications?: number;
  };
}

/* ══════════════════════════════════════════════════════════
   Main calculation
   ══════════════════════════════════════════════════════════ */

export function calculatePrice(
  config: ProjectConfiguration,
  allOptions: ProductOption[],
  complexity: ComplexityFactors = { laborComplexity: "standard", plumbingRelocation: false, customModifications: false },
  manualAdjustment: number = 0,
  manualAdjustmentNotes: string = "",
  overrides?: PricingOverrides,
): PriceBreakdown {
  const optById = (id: string) => allOptions.find(o => o.id === id);
  const bathroomMaterials = config.bathroomMaterials ?? [];
  const selectedGrabBars = config.selectedGrabBars ?? [];
  const selectedAddOns = config.selectedAddOns ?? [];
  const customQuoteItems = config.customQuoteItems ?? [];

  const effectiveBasePrices = overrides?.systemBasePrices ?? SYSTEM_BASE_PRICES;
  const effectivePresets = overrides?.dimensionPresets ?? DIMENSION_PRESETS;
  const effectiveMultipliers = overrides?.materialMultipliers ?? MATERIAL_MULTIPLIERS;
  const effectiveComplexity = overrides?.complexityAdders;

  // 1. Base system price
  const systemType = (config.bathroomSystemType || "Walk-in Shower") as BathroomSystemType;
  const baseSystemPrice = (effectiveBasePrices as Record<string, number>)[systemType] ?? 0;

  // 2. Dimension adjustment
  const width = parseInt(config.baseWidth || "30") || 30;
  const length = parseInt(config.baseLength || "60") || 60;
  // Check overridden presets first
  const presetMatch = effectivePresets.find(p => p.width === width && p.length === length);
  const dimensionAdjustment = presetMatch
    ? presetMatch.adjustment
    : Math.max(0, Math.round((width * length - BASELINE_SQIN) * CUSTOM_SQIN_RATE));
  const dimLabel = `${width}" × ${length}"`;

  // 3. Material adjustment (multiplier on base, minus the base itself)
  const material = (bathroomMaterials.length > 0 ? bathroomMaterials[0] : "Sentrel") as BathroomMaterial;
  const multiplier = (effectiveMultipliers as Record<string, number>)[material] ?? 1.0;
  const materialAdjustment = Math.round(baseSystemPrice * (multiplier - 1));

  // 4. Build component totals
  const componentDetails: { name: string; amount: number; pricingLabel?: string }[] = [];
  const addComponent = (label: string, id: string) => {
    if (!id) return;
    const opt = optById(id);
    if (!opt) return;
    const amt = parsePricingImpact(opt.pricingImpact);
    const pricingLabel = opt.pricingImpact?.trim() || "Included";
    componentDetails.push({ name: opt.displayName, amount: amt, pricingLabel });
  };

  addComponent("Wall System", config.selectedWallSystem);
  addComponent("Base", config.selectedBase);
  addComponent("Glass", config.selectedGlass);
  addComponent("Door", config.selectedDoor);
  addComponent("Fixtures", config.selectedFixture);
  selectedGrabBars.forEach(id => addComponent("Grab Bar", id));
  selectedAddOns.forEach(id => addComponent("Add-on", id));

  // Custom quote items
  customQuoteItems.forEach(ci => {
    const amt = parseFloat(ci.priceAdjustment.replace(/[^0-9.\-+]/g, "")) || 0;
    if (amt !== 0) componentDetails.push({ name: ci.itemName, amount: amt });
  });

  const componentTotal = componentDetails.reduce((sum, c) => sum + c.amount, 0);

  // 5. Complexity
  const complexityDetails: string[] = [];
  let complexityAdjustment = 0;

  const laborAdder = effectiveComplexity?.[complexity.laborComplexity] ?? LABOR_COMPLEXITY_ADDERS[complexity.laborComplexity];
  if (laborAdder > 0) {
    complexityAdjustment += laborAdder;
    complexityDetails.push(`${complexity.laborComplexity} labor: +$${laborAdder.toLocaleString()}`);
  }
  const plumbingAdder = effectiveComplexity?.plumbingRelocation ?? PLUMBING_RELOCATION_ADDER;
  if (complexity.plumbingRelocation) {
    complexityAdjustment += plumbingAdder;
    complexityDetails.push(`Plumbing relocation: +$${plumbingAdder.toLocaleString()}`);
  }
  const customModAdder = effectiveComplexity?.customModifications ?? CUSTOM_MODIFICATIONS_ADDER;
  if (complexity.customModifications) {
    complexityAdjustment += customModAdder;
    complexityDetails.push(`Custom modifications: +$${customModAdder.toLocaleString()}`);
  }

  // 6. Final
  const calculatedPrice = Math.max(0,
    baseSystemPrice + dimensionAdjustment + materialAdjustment +
    componentTotal + complexityAdjustment
  );
  const finalTotal = Math.max(0, calculatedPrice + manualAdjustment);

  return {
    baseSystemPrice,
    dimensionAdjustment,
    materialAdjustment,
    componentTotal,
    complexityAdjustment,
    manualAdjustment,
    manualAdjustmentNotes,
    calculatedPrice,
    finalTotal,
    systemType,
    dimensions: dimLabel,
    material,
    componentDetails,
    complexityDetails,
  };
}
